import type { ReportTheme, ThemeBlock } from '../../types/reportTheme'
import { FONT_STACK, str, LogoSlot, ThemeCover } from './ThemeCover'
import { resolveChartPalette } from '../reports/chartPalette'

const SAMPLE_KPIS = [
  { label: 'Sessions', value: '48,2k', delta: '+12,4 %' },
  { label: 'Conversions', value: '1 284', delta: '+8,1 %' },
  { label: 'Taux de conv.', value: '2,7 %', delta: '+0,3 pt' },
]
const SAMPLE_BARS = [62, 88, 54, 96, 71, 83]

/**
 * Aperçu léger du thème : une page de garde + une page intérieure fictives,
 * pilotées par les tokens du thème (couleurs, police, logos, blocs activés).
 * C'est une MAQUETTE de branding — pas le moteur de rendu réel du rapport (PR B).
 * La couverture réutilise le composant partagé `ThemeCover` (mêmes tokens que le rendu de snapshot).
 */
export function ThemePreview({ theme }: { theme: ReportTheme }) {
  const cover = theme.cover as ThemeBlock
  const header = theme.header as ThemeBlock
  const footer = theme.footer as ThemeBlock
  const intro = theme.intro as ThemeBlock

  const font = FONT_STACK[theme.fontFamily] ?? FONT_STACK.sans
  const primary = theme.primaryColor
  const secondary = theme.secondaryColor
  const palette = resolveChartPalette(theme.chartPalette)

  const introText = str(intro.text) ?? 'Synthèse des performances du canal sur la période, faits marquants et recommandations.'

  return (
    <div className="flex flex-col items-center gap-6" style={{ fontFamily: font }}>
      {/* Page de garde — affichée seulement si activée (valeurs fictives).
          ThemeCover est figé 1100×1556 ; on le réduit pour l'aperçu (largeur 420). */}
      {cover.enabled ? (
        <div
          className="overflow-hidden rounded-lg shadow-[0_8px_30px_rgba(16,24,64,0.16)]"
          style={{ width: 420, height: 420 * (1556 / 1100) }}
        >
          <div style={{ transform: `scale(${420 / 1100})`, transformOrigin: 'top left' }}>
            <ThemeCover theme={theme} clientLabel="Client Exemple · exemple.fr" periodLabel="1 – 31 juillet 2026" />
          </div>
        </div>
      ) : null}

      {/* Page intérieure */}
      <div className="w-full max-w-[420px] overflow-hidden rounded-lg bg-white shadow-[0_8px_30px_rgba(16,24,64,0.12)]">
        <div className="flex aspect-[210/297] flex-col">
          {/* En-tête */}
          <div
            className="flex items-center justify-between px-6 py-3"
            style={header.enabled === false ? { borderBottom: '1px solid #E2E4ED' } : { backgroundColor: primary, color: '#fff' }}
          >
            <div className="flex items-center gap-2">
              <LogoSlot url={theme.clientLogoUrl} alt="Logo client (en-tête)" label="LOGO" size="xs" onDark={header.enabled !== false} />
              <span className="text-xs font-semibold" style={header.enabled === false ? { color: secondary } : undefined}>
                Client Exemple
              </span>
            </div>
            <span className="text-[10px] opacity-80" style={header.enabled === false ? { color: '#8B8FA7' } : undefined}>
              Juillet 2026
            </span>
          </div>

          <div className="flex flex-1 flex-col gap-4 p-6">
            {intro.enabled ? (
              <div className="rounded-md p-3 text-[11px] leading-relaxed" style={{ backgroundColor: `${primary}14`, color: secondary }}>
                {introText}
              </div>
            ) : null}

            <h3 className="text-sm font-bold" style={{ color: secondary }}>Vue d’ensemble</h3>

            <div className="grid grid-cols-3 gap-2">
              {SAMPLE_KPIS.map((kpi) => (
                <div key={kpi.label} className="rounded-md border border-[#EEF0F6] p-2.5">
                  <p className="text-[9px] uppercase tracking-wide text-[#8B8FA7]">{kpi.label}</p>
                  <p className="mt-1 text-base font-bold leading-none" style={{ color: primary }}>{kpi.value}</p>
                  <p className="mt-1 text-[9px] font-medium" style={{ color: '#10B981' }}>{kpi.delta}</p>
                </div>
              ))}
            </div>

            {/* Mini graphe piloté par la palette */}
            <div className="rounded-md border border-[#EEF0F6] p-3">
              <p className="mb-2 text-[10px] font-medium text-[#8B8FA7]">Évolution mensuelle</p>
              <div className="flex h-20 items-end gap-1.5">
                {SAMPLE_BARS.map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t-sm"
                    style={{ height: `${h}%`, backgroundColor: palette[i % palette.length] }}
                  />
                ))}
              </div>
            </div>

            <div className="mt-auto flex gap-1.5">
              {palette.slice(0, 6).map((c, i) => (
                <span key={i} className="h-3 flex-1 rounded-full" style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>

          {/* Pied de page */}
          {footer.enabled ? (
            <div className="flex items-center justify-between border-t border-[#EEF0F6] px-6 py-2.5 text-[9px] text-[#8B8FA7]">
              <span>{str(footer.mentions) ?? 'Document confidentiel'}</span>
              <span>Page 1 · 31/07/2026</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
